import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle2, Clock, AlarmClockOff, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Reminders control center (owner spec §9-§10): Upcoming | Today | Completed | Snoozed
   with Snooze (1 hour / tomorrow / pick), Mark Done, Edit time, Open Trip. */

const VIEWS = ["upcoming", "today", "completed", "snoozed"];

export default function RemindersPanel() {
  const [view, setView] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // reminder id being re-timed
  const [editWhen, setEditWhen] = useState("");

  const load = useCallback(() => api.get(`/reminders?view=${view}`).then((r) => setItems(r.data.items)).catch(() => {}), [view]);
  useEffect(() => { load(); }, [load]);

  const done = async (rid) => { try { await api.post(`/reminders/${rid}/done`); load(); } catch { toast.error("Failed"); } };
  const snooze = async (rid, until) => {
    try { await api.post(`/reminders/${rid}/snooze`, { until: until.toISOString() }); toast.success("Snoozed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async (rid) => {
    try { await api.put(`/reminders/${rid}`, { remind_at: new Date(editWhen).toISOString() }); setEditing(null); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="reminders-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`reminders-view-${v}`}
            className={`px-3 py-1.5 text-sm border capitalize ${view === v ? "border-indigo-600 bg-indigo-50 font-700 text-indigo-800" : "border-zinc-300"}`}>{v}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6">No {view} reminders. Set one from any trip with the 🔔 button.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="border border-zinc-300 bg-white p-3" data-testid={`reminders-item-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Bell className={`w-4 h-4 shrink-0 ${r.status === "sent" ? "text-amber-600" : "text-indigo-600"}`} />
                <span className="font-700 text-sm">{r.title}</span>
                <span className={`text-[11px] px-1.5 py-0.5 border capitalize ${r.status === "completed" ? "bg-zinc-50 text-zinc-500" : r.status === "sent" ? "bg-amber-50 text-amber-700 border-amber-300" : r.status === "snoozed" ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}>{r.status}</span>
                <span className="text-xs text-muted-foreground ml-auto font-mono">{new Date(r.remind_at).toLocaleString()}</span>
              </div>
              {r.message && <p className="text-xs text-zinc-600 mt-1 ml-6">{r.message}</p>}
              <div className="flex flex-wrap items-center gap-3 mt-2 ml-6 text-xs">
                <span className="text-muted-foreground">{r.in_app ? "🔔 In app" : ""}{r.in_app && r.email ? " · " : ""}{r.email ? "✉️ Email" : ""}</span>
                {r.trip_id && <Link to={`/trips/${r.trip_id}`} data-testid={`reminder-open-trip-${r.id}`} className="font-700 text-indigo-700 hover:underline">Open trip</Link>}
                {r.status !== "completed" && (
                  <>
                    <button onClick={() => done(r.id)} data-testid={`reminder-done-${r.id}`} className="inline-flex items-center gap-1 font-700 text-emerald-700 hover:underline"><CheckCircle2 className="w-3.5 h-3.5" /> Mark done</button>
                    <button onClick={() => snooze(r.id, new Date(Date.now() + 36e5))} data-testid={`reminder-snooze-1h-${r.id}`} className="inline-flex items-center gap-1 font-700 text-sky-700 hover:underline"><Clock className="w-3.5 h-3.5" /> Snooze 1h</button>
                    <button onClick={() => { const t = new Date(Date.now() + 864e5); t.setHours(9, 0, 0, 0); snooze(r.id, t); }} data-testid={`reminder-snooze-tomorrow-${r.id}`} className="inline-flex items-center gap-1 font-700 text-sky-700 hover:underline"><AlarmClockOff className="w-3.5 h-3.5" /> Tomorrow</button>
                    <button onClick={() => { setEditing(editing === r.id ? null : r.id); setEditWhen(""); }} data-testid={`reminder-edit-${r.id}`} className="inline-flex items-center gap-1 font-700 text-zinc-600 hover:underline"><Pencil className="w-3.5 h-3.5" /> Edit time</button>
                  </>
                )}
              </div>
              {editing === r.id && (
                <div className="flex items-center gap-2 mt-2 ml-6">
                  <input type="datetime-local" value={editWhen} onChange={(e) => setEditWhen(e.target.value)} data-testid={`reminder-edit-when-${r.id}`}
                    className="border border-zinc-300 px-2 py-1.5 text-sm bg-white" />
                  <button onClick={() => saveEdit(r.id)} disabled={!editWhen} data-testid={`reminder-edit-save-${r.id}`}
                    className="bg-indigo-700 text-white px-3 py-1.5 text-xs font-700 disabled:opacity-50">Save</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
